import axios, { AxiosError } from "axios";
import axiosInstance from "config/axiosConfig";

interface Address {
  addressLineOne: string;
  addressLineTwo: string;
  city: string;
  state: string;
  postalCode: string;
}

interface Source {
  referral: string;
  channel: string;
}

export interface LeadV2 {
  id: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  city: string;
  source: Source;
  pilId: string;
  metadata: string;
}

export interface ContactV2Request {
  page?: number;
  pageSize?: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  cities?: string[];
  sortBy?: string;
  sortOrder?: string;
  paginationToken?: string;
}

export interface PaginationMetadata {
  currentPage: number;
  pageSize: number;
  returnedRecords: number;
  hasNext: boolean;
  hasPrevious: boolean;
  nextPageToken: string | null;
  previousPageToken: string | null;
}

export interface ContactV2Response {
  contacts: LeadV2[];
  pagination: PaginationMetadata;
}

export const API_PATHS_V2 = {
  CONTACT_V2_SEARCH: "/v2/contact/search",
};

export type ErrorType = {
  error: string;
  message: string;
  status: number;
  timestamp: string;
};

// Handle errors to extract backend error messages
const handleError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ErrorType>;
    return axiosError.response?.data?.message || "An unknown error occurred";
  }
  return "An unknown error occurred";
};

// Search contacts using V2 API with pagination and filtering
const searchContactsV2 = async (
  request: ContactV2Request
): Promise<ContactV2Response> => {
  try {
    const response = await axiosInstance.post(API_PATHS_V2.CONTACT_V2_SEARCH, {
      page: request.page || 1,
      pageSize: request.pageSize || 20,
      firstName: request.firstName,
      lastName: request.lastName,
      email: request.email,
      phoneNumber: request.phoneNumber,
      cities: request.cities,
      sortBy: request.sortBy || "createdAt",
      sortOrder: request.sortOrder || "desc",
      paginationToken: request.paginationToken,
    });
    return response.data;
  } catch (error) {
    throw new Error(handleError(error));
  }
};

export {
  searchContactsV2,
};
